import { Link } from "@tanstack/react-router";
import { Minus, Plus, Trash2, ShoppingBag, Truck, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart, selectSubtotalCents, selectCount, freeShippingRemainingCents } from "@/lib/cart/cart-store";
import { formatEuro } from "@/hooks/useShippingConfig";
import { FREE_SHIPPING_OVER_CENTS_FALLBACK, RETURNS_DAYS_DEFAULT } from "@/lib/site-config";
import { trackCart, trackCheckout } from "@/lib/client-errors/tracker";

export function CartDrawer() {
  const isOpen = useCart((s) => s.isOpen);
  const close = useCart((s) => s.close);
  const items = useCart((s) => s.items);
  const subtotal = useCart(selectSubtotalCents);
  const count = useCart(selectCount);
  const updateQty = useCart((s) => s.updateQty);
  const remove = useCart((s) => s.remove);

  const threshold = FREE_SHIPPING_OVER_CENTS_FALLBACK;
  const remainingCents = freeShippingRemainingCents(subtotal, threshold);
  const pct = threshold ? Math.min(100, Math.round((subtotal / threshold) * 100)) : 0;
  const currency = items[0]?.currency ?? "EUR";

  return (
    <Sheet open={isOpen} onOpenChange={(o) => (o ? undefined : close())}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 gap-0">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-[#172554]">
              <ShoppingBag className="h-4 w-4" />
              Your cart
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                {count}
              </span>
            </span>
          </SheetTitle>
        </SheetHeader>

        {threshold != null && (
          <div className="border-b bg-sky-pop/15 px-5 py-3 text-xs text-ink">
            {remainingCents === 0 ? (
              <div className="flex items-center gap-2 font-semibold text-emerald-700">
                <Truck className="h-3.5 w-3.5" /> You've unlocked free worldwide shipping.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span>
                    Add <b>{formatEuro(remainingCents ?? 0)}</b> more for free shipping
                  </span>
                  <span className="tabular-nums text-slate-500">
                    {formatEuro(subtotal)} / {formatEuro(threshold)}
                  </span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-white/70 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-royal transition-[width] duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <EmptyState onClose={close} />
          ) : (
            <ul className="divide-y">
              {items.map((it) => (
                <li key={it.key} className="flex gap-3 p-4">
                  <Link
                    to="/p/$slug"
                    params={{ slug: it.slug }}
                    onClick={close}
                    className="h-20 w-20 shrink-0 overflow-hidden rounded-md border bg-white"
                  >
                    {it.image ? (
                      <img src={it.image} alt={it.title} className="h-full w-full object-contain p-1" />
                    ) : null}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to="/p/$slug"
                      params={{ slug: it.slug }}
                      onClick={close}
                      className="line-clamp-2 text-sm font-semibold text-[#172554] hover:text-royal"
                    >
                      {it.title}
                    </Link>
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      {it.brand ? <span>{it.brand} · </span> : null}
                      <span className="font-mono">{it.sku}</span>
                      {it.variantLabel ? <span> · {it.variantLabel}</span> : null}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center rounded-md border">
                        <button
                          aria-label="Decrease"
                          onClick={() => {
                            trackCart("qty_decrease", { sku: it.sku, from: it.qty, to: it.qty - 1 });
                            updateQty(it.key, it.qty - 1);
                          }}
                          className="grid h-7 w-7 place-items-center hover:bg-slate-50"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="min-w-6 px-2 text-center text-sm font-semibold tabular-nums">
                          {it.qty}
                        </span>
                        <button
                          aria-label="Increase"
                          onClick={() => {
                            trackCart("qty_increase", { sku: it.sku, from: it.qty, to: it.qty + 1 });
                            updateQty(it.key, it.qty + 1);
                          }}
                          disabled={it.maxQty != null && it.qty >= it.maxQty}
                          className="grid h-7 w-7 place-items-center hover:bg-slate-50 disabled:opacity-40"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-[#172554] tabular-nums">
                          {formatEuro(it.unitPriceCents * it.qty)}
                        </span>
                        <button
                          aria-label="Remove"
                          onClick={() => {
                            trackCart("remove", { sku: it.sku, qty: it.qty });
                            remove(it.key);
                          }}
                          className="text-slate-400 hover:text-rose-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t bg-white px-5 py-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span className="text-lg font-extrabold text-[#172554] tabular-nums">
                {formatEuro(subtotal)}
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-slate-400">Shipping calculated at checkout · VAT incl.</p>
            <Link
              to={"/checkout" as never}
              onClick={() => {
                trackCheckout("proceed_click_drawer", {
                  subtotal_cents: subtotal,
                  items: items.length,
                });
                close();
              }}
              className="mt-3 flex h-11 items-center justify-center rounded-md bg-[#172554] font-semibold text-white hover:bg-royal"
            >
              Checkout · {formatEuro(subtotal)}
            </Link>
            <Link
              to={"/cart" as never}
              onClick={close}
              className="mt-2 flex h-9 items-center justify-center rounded-md border text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              View full cart
            </Link>
            <div className="mt-3 flex items-center justify-between text-[10px] text-slate-500">
              <span className="inline-flex items-center gap-1"><Truck className="h-3 w-3" /> Worldwide shipping</span>
              <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> {RETURNS_DAYS_DEFAULT}-day returns</span>
              <span className="uppercase tracking-wider">{currency}</span>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function EmptyState({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-slate-100 text-slate-400">
        <ShoppingBag className="h-6 w-6" />
      </div>
      <div>
        <p className="text-sm font-semibold text-[#172554]">Your cart is empty</p>
        <p className="mt-1 text-xs text-slate-500">Add parts to see them here — we'll keep them safe across pages and devices.</p>
      </div>
      <Link
        to={"/shop" as never}
        onClick={onClose}
        className="mt-2 inline-flex h-10 items-center rounded-md bg-[#172554] px-4 text-sm font-semibold text-white hover:bg-royal"
      >
        Browse parts
      </Link>
    </div>
  );
}